import { useModal } from '@ebay/nice-modal-react';
import { Room } from 'matrix-js-sdk/src';
import moment from 'moment';
import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useToast from 'contexts/ToastProvider/useToast';
import { getRoomMetadata } from 'modules/RoomsListItem/utils';
import logger from 'utils/logging/faro';
import { SetState } from 'utils/types';

import useCreateNewRoomForm from './useCreateRoomForm';
import { AliasStatusEnum, getDigitsFromRoomAlias, getRandomFourDigitString, RoomFormType } from './utils';
import { getInviteInformation } from '../CreateRoomStepModal/utils';
import { CreateNewRoomStepsEnum } from '../utils';

export interface CreateNewRoomDataContextType {
  currentStep: CreateNewRoomStepsEnum;
  setCurrentStep: SetState<CreateNewRoomStepsEnum>;
  isScheduledInitially: boolean;
  roomForm: UseFormReturn<RoomFormType, undefined>;
  digitsPostfix: string;
  setDigitsPostfix: SetState<string>;
  hasAliasChanged: boolean;
  setHasAliasChanged: SetState<boolean>;
  aliasStatus: AliasStatusEnum;
  setAliasStatus: SetState<AliasStatusEnum>;
  isScheduling: boolean;
  setIsScheduling: SetState<boolean>;
  isSchedulingAvailable: boolean;
  hasToReturnToReview: boolean;
  setHasToReturnToReview: SetState<boolean>;
  stepToGoBackTo: CreateNewRoomStepsEnum | null;
  setStepToGoBackTo: SetState<CreateNewRoomStepsEnum | null>;
  handleExit: () => void;
  isInviteInformationLoading: boolean;
  defaultEmails: string[];
  defaultInvitations: Record<string, string>;
  roomToEdit?: Room;
}

export const CreateNewRoomDataContext = createContext<CreateNewRoomDataContextType | null>(null);

export interface CreateNewRoomDataProviderProps {
  room?: Room;
  isScheduledInitially?: boolean;
  isPrivateInitially?: boolean;
}

const CreateNewRoomDataProvider = ({
  children,
  isScheduledInitially = false,
  isPrivateInitially = false,
  room,
}: PropsWithChildren<CreateNewRoomDataProviderProps>) => {
  const { t } = useTranslation();
  const { remove } = useModal();
  const { pushToast } = useToast();
  const [currentStep, setCurrentStep] = useState(CreateNewRoomStepsEnum.CREATE_ROOM);
  const [hasAliasChanged, setHasAliasChanged] = useState(false);

  const [aliasStatus, setAliasStatus] = useState(AliasStatusEnum.UNKNOWN);
  const [hasToReturnToReview, setHasToReturnToReview] = useState(false);
  const [stepToGoBackTo, setStepToGoBackTo] = useState<CreateNewRoomStepsEnum | null>(null);
  const [isInviteInformationLoading, setIsInviteInformationLoading] = useState(false);
  const [defaultEmails, setDefaultEmails] = useState<string[]>([]);
  const [defaultInvitations, setDefaultInvitations] = useState<Record<string, string>>({});

  const metadata = room && getRoomMetadata(room);
  const isSchedulingAvailable =
    !room || (!!metadata?.scheduledToStartAt && moment(metadata?.scheduledToStartAt).isAfter(moment()));

  const roomForm = useCreateNewRoomForm({ room, isSchedulingAvailable, isPrivateInitially });
  const [isScheduling, setIsScheduling] = useState(isScheduledInitially || !!metadata?.scheduledToStartAt);

  const initialDigits = getDigitsFromRoomAlias(room);
  const [digitsPostfix, setDigitsPostfix] = useState(initialDigits ? initialDigits : getRandomFourDigitString());

  const handleExit = useCallback(() => {
    if (!roomForm.formState.isDirty) return remove();
    setStepToGoBackTo(currentStep);
    setCurrentStep(CreateNewRoomStepsEnum.DISCARD);
  }, [currentStep, remove, roomForm.formState.isDirty]);

  useEffect(() => {
    if (!room || !isSchedulingAvailable) return;

    setIsInviteInformationLoading(true);

    getInviteInformation(room)
      .then(({ message, participants }) => {
        const emails: string[] = [];
        const byEmail: Record<string, string> = {};
        for (const { id, address } of participants) {
          if (!address) continue;
          emails.push(address);
          if (id) byEmail[address] = id;
        }

        setDefaultEmails(emails);
        setDefaultInvitations(byEmail);
        roomForm.reset(
          { ...roomForm.getValues(), participants: emails, invitationMessage: message },
          { keepDirty: true, keepTouched: true, keepErrors: true }
        );
      })
      .catch((error) => {
        pushToast({
          title: t('invitation_details_unavailable'),
          description: t('invitation_details_unavailable_description'),
          variant: 'info',
        });
        logger.error('Error getting invite information:', error);
      })
      .finally(() => setIsInviteInformationLoading(false));
  }, [isSchedulingAvailable, pushToast, room, roomForm, t]);

  return (
    <CreateNewRoomDataContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        isScheduledInitially,
        roomForm,
        digitsPostfix,
        setDigitsPostfix,
        hasAliasChanged,
        setHasAliasChanged,
        aliasStatus,
        setAliasStatus,
        isScheduling,
        setIsScheduling,
        isSchedulingAvailable,
        hasToReturnToReview,
        setHasToReturnToReview,
        setStepToGoBackTo,
        stepToGoBackTo,
        handleExit,
        isInviteInformationLoading,
        defaultEmails,
        defaultInvitations,
        roomToEdit: room,
      }}
    >
      {children}
    </CreateNewRoomDataContext.Provider>
  );
};

export default CreateNewRoomDataProvider;
